/**
 * Invoice API client — wrappers for `/v1/invoices/*` endpoints.
 *
 * Backend: `internal/payment/http_app/invoices.go` (PR #112, commit 4850575).
 *
 * Routes:
 *   POST /v1/invoices/issue        — manager creates invoice for юр.лицо
 *   GET  /v1/invoices              — list the tenant's invoices (newest first)
 *   GET  /v1/invoices/{id}         — invoice detail
 *   POST /v1/invoices/{id}/send    — transition draft → sent
 *   POST /v1/invoices/{id}/pay     — settle online via T-Bank (returns pay-URL)
 *   POST /v1/invoices/{id}/mark-paid — admin confirms bank transfer
 *   POST /v1/invoices/{id}/cancel  — void invoice (with reason)
 */
import { customFetch } from './openapi-fetch.js';
import type {
  Invoice,
  InvoiceListResponse,
  IssueInvoiceRequest,
  CancelInvoiceRequest,
  MarkInvoicePaidRequest,
  Payment,
  PayInvoiceRequest,
} from '@hsc/types';

export async function issueInvoice(req: IssueInvoiceRequest): Promise<Invoice> {
  return customFetch<Invoice>({
    url: '/invoices/issue',
    method: 'POST',
    data: req,
  });
}

/** List the tenant's invoices (newest first). Management surface. */
export async function listInvoices(): Promise<InvoiceListResponse> {
  return customFetch<InvoiceListResponse>({
    url: '/invoices',
    method: 'GET',
  });
}

/**
 * Settle an invoice online via the T-Bank payment form (#238). Requires a
 * stable `Idempotency-Key` (client-side UUID) so re-clicking «Оплатить»
 * replays the same intent. The returned Payment carries `tbank_payment_url`
 * — redirect the browser there via `redirectToTbank`.
 */
export async function payInvoice(
  id: string,
  idempotencyKey: string,
  req: PayInvoiceRequest = {},
): Promise<Payment> {
  return customFetch<Payment>({
    url: `/invoices/${encodeURIComponent(id)}/pay`,
    method: 'POST',
    data: req,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export async function getInvoice(id: string): Promise<Invoice> {
  return customFetch<Invoice>({
    url: `/invoices/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

export async function sendInvoice(id: string): Promise<Invoice> {
  return customFetch<Invoice>({
    url: `/invoices/${encodeURIComponent(id)}/send`,
    method: 'POST',
  });
}

export async function cancelInvoice(
  id: string,
  req: CancelInvoiceRequest,
): Promise<Invoice> {
  return customFetch<Invoice>({
    url: `/invoices/${encodeURIComponent(id)}/cancel`,
    method: 'POST',
    data: req,
  });
}

export async function markInvoicePaid(
  id: string,
  req: MarkInvoicePaidRequest,
): Promise<Invoice> {
  return customFetch<Invoice>({
    url: `/invoices/${encodeURIComponent(id)}/mark-paid`,
    method: 'POST',
    data: req,
  });
}
